import { useId, useMemo, useState } from 'react';

import {
  Badge,
  Button,
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@monksflow/monks-ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { ChevronDownIcon } from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import type { TFunction } from 'i18next';

import type { McpCatalogResponseDto } from '@inference.engine/api-client/web';

import { FormSidePanel } from '@/shared/ui/layout/FormSidePanel';

import { useCreateCatalogMcp, useUpdateCatalogMcp } from '../../model/useCatalogMcp';
import { AUTH_TYPE_LABEL_KEY, AUTH_TYPE_OPTIONS, type McpAuthType } from './authTypes';
import { credentialFields } from './credentialFields';

// A factory, not a module-scope const: building the schema at import time would bake
// in whatever language was active then, and the messages could never follow a change.
const buildMcpCatalogSchema = (t: TFunction<'validation'>) =>
  z.object({
    name: z.string().min(1, t('admin.mcpCatalogPanel.nameIsRequired')).trim(),
    description: z.string().trim().optional(),
    url: z.url(t('admin.mcpCatalogPanel.mustBeAValidUrl')),
    authType: z.enum(['static_headers', 'oauth', 'no_auth']),
    // Free text from a provider's console: no shape we could usefully assert. The secret is
    // write-only, so an empty field means "leave whatever is stored alone", not "no secret".
    oauthClientId: z.string().trim().optional(),
    oauthClientSecret: z.string().optional(),
  });

export type McpCatalogFormData = z.infer<ReturnType<typeof buildMcpCatalogSchema>>;

const RequiredMark = () => <span className="text-destructive"> *</span>;

const seedForm = (entry: McpCatalogResponseDto | null): McpCatalogFormData => ({
  name: entry?.name ?? '',
  description: entry?.description ?? '',
  url: entry?.url ?? '',
  authType: entry?.authType ?? 'static_headers',
  oauthClientId: entry?.oauthClientId ?? '',
  // Never seeded: the secret is not returned, and an empty field is what makes an untouched
  // form leave the stored one alone.
  oauthClientSecret: '',
});

interface McpCatalogFormSheetProps {
  open: boolean;
  /** The entry being edited, or null to add one. */
  entry: McpCatalogResponseDto | null;
  onClose: () => void;
}

/**
 * Add and edit surface for a catalog entry, as a right-side sheet rather than a
 * centre modal — the treatment every add/modify form in the redesign uses.
 *
 * The host remounts this on each open (a `key` it bumps), which is what seeds the
 * form from `entry` and clears an abandoned draft without an effect watching the
 * open flag. Closing leaves it mounted so the exit animation plays over the values
 * it held rather than over defaults.
 */
export function McpCatalogFormSheet({ open, entry, onClose }: McpCatalogFormSheetProps) {
  const { t } = useTranslation('admin');
  const { t: tValidation } = useTranslation('validation');
  const fieldId = useId();

  const createMutation = useCreateCatalogMcp();
  const updateMutation = useUpdateCatalogMcp();

  const formSchema = useMemo(() => buildMcpCatalogSchema(tValidation), [tValidation]);
  const form = useForm<McpCatalogFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: seedForm(entry),
    mode: 'onTouched',
  });

  const hasStoredCredentials = Boolean(entry?.oauthClientId ?? entry?.hasOauthClientSecret);
  // Opened for an entry that already has credentials, so the section starts expanded rather
  // than hiding the thing the admin most likely came to change.
  const [credentialsOpen, setCredentialsOpen] = useState(hasStoredCredentials);

  const authType = form.watch('authType');

  // Warn only when the pending edit would actually change our identity at the authorization
  // server, which is what invalidates the tokens people already hold. Derived from the same
  // function that builds the request, so the warning cannot promise something the save does
  // not do. Nobody is connected to an entry that does not exist yet, so creating never warns.
  const [typedClientId, typedClientSecret] = form.watch(['oauthClientId', 'oauthClientSecret']);
  const credentialsWillChange =
    entry !== null &&
    Object.keys(
      credentialFields(
        { oauthClientId: typedClientId, oauthClientSecret: typedClientSecret },
        entry,
      ),
    ).length > 0;

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  const handleSubmit = form.handleSubmit((data) => {
    const body = {
      name: data.name,
      description: data.description || undefined,
      url: data.url,
      authType: data.authType,
      ...(data.authType === 'oauth' ? credentialFields(data, entry) : {}),
    };

    if (entry) {
      updateMutation.mutate({ id: entry.id, body }, { onSuccess: onClose });
    } else {
      createMutation.mutate({ body }, { onSuccess: onClose });
    }
  });

  return (
    <FormSidePanel
      open={open}
      onOpenChange={(next) => !next && onClose()}
      title={
        entry ? t('admin.mcpCatalogPanel.editMcpServer') : t('admin.mcpCatalogPanel.addMcpServer')
      }
      className="w-[560px] gap-sm"
      footer={
        <>
          <Button variant="outline" size="lg" onClick={onClose} disabled={isSubmitting}>
            {t('admin.mcpCatalogPanel.cancel')}
          </Button>
          <Button
            variant="default"
            size="lg"
            onClick={() => void handleSubmit()}
            disabled={isSubmitting}
          >
            {entry ? t('admin.mcpCatalogPanel.update') : t('admin.mcpCatalogPanel.addMcpServer')}
          </Button>
        </>
      }
    >
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void handleSubmit();
        }}
        className="flex flex-col gap-md"
      >
        <Field data-invalid={Boolean(form.formState.errors.name) || undefined}>
          <FieldLabel htmlFor={`${fieldId}-name`}>
            {t('admin.mcpCatalogPanel.name')}
            <RequiredMark />
          </FieldLabel>
          <Input
            id={`${fieldId}-name`}
            {...form.register('name')}
            placeholder={t('admin.mcpCatalogPanel.eGAtlassian')}
            disabled={isSubmitting}
            aria-invalid={Boolean(form.formState.errors.name) || undefined}
          />
          {form.formState.errors.name && (
            <FieldError>{form.formState.errors.name.message}</FieldError>
          )}
        </Field>

        <Field>
          <FieldLabel htmlFor={`${fieldId}-description`}>
            {t('admin.mcpCatalogPanel.description')}
          </FieldLabel>
          <Textarea
            id={`${fieldId}-description`}
            {...form.register('description')}
            placeholder={t('admin.mcpCatalogPanel.whatThisMcpServerProvides')}
            disabled={isSubmitting}
            rows={3}
          />
        </Field>

        <Field data-invalid={Boolean(form.formState.errors.url) || undefined}>
          <FieldLabel htmlFor={`${fieldId}-url`}>
            {t('admin.mcpCatalogPanel.url')}
            <RequiredMark />
          </FieldLabel>
          <Input
            id={`${fieldId}-url`}
            {...form.register('url')}
            placeholder="https://mcp.example.com/v1/mcp"
            disabled={isSubmitting}
            aria-invalid={Boolean(form.formState.errors.url) || undefined}
          />
          {form.formState.errors.url && (
            <FieldError>{form.formState.errors.url.message}</FieldError>
          )}
        </Field>

        <Controller
          name="authType"
          control={form.control}
          render={({ field }) => (
            <Field>
              <FieldLabel htmlFor={`${fieldId}-auth-type`}>
                {t('admin.mcpCatalogPanel.authType')}
                <RequiredMark />
              </FieldLabel>
              <Select
                value={field.value}
                onValueChange={(next) => field.onChange(next as McpAuthType)}
                disabled={isSubmitting}
              >
                <SelectTrigger id={`${fieldId}-auth-type`} className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AUTH_TYPE_OPTIONS.map((option) => (
                    <SelectItem key={option} value={option}>
                      {t(AUTH_TYPE_LABEL_KEY[option])}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}
        />

        {authType === 'oauth' && (
          <div className="flex flex-col gap-sm">
            <div className="flex items-center gap-sm">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setCredentialsOpen((isOpen) => !isOpen)}
                aria-expanded={credentialsOpen}
              >
                {t('admin.mcpCatalogPanel.clientRegistration')}
                <ChevronDownIcon
                  className={`size-4 transition-transform duration-fast ease-out ${
                    credentialsOpen ? 'rotate-180' : ''
                  }`}
                />
              </Button>
              {hasStoredCredentials && (
                <Badge variant="positive" size="sm" roundness="full">
                  {t('admin.mcpCatalogPanel.clientRegistrationConfigured')}
                </Badge>
              )}
            </div>

            {credentialsOpen && (
              <div className="flex flex-col gap-md">
                <p className="text-body-sm text-muted-foreground">
                  {t('admin.mcpCatalogPanel.mostServersRegisterAutomatically')}
                </p>
                <Field>
                  <FieldLabel htmlFor={`${fieldId}-client-id`}>
                    {t('admin.mcpCatalogPanel.clientId')}
                  </FieldLabel>
                  <Input
                    id={`${fieldId}-client-id`}
                    {...form.register('oauthClientId')}
                    disabled={isSubmitting}
                  />
                </Field>
                <Field>
                  <FieldLabel htmlFor={`${fieldId}-client-secret`}>
                    {t('admin.mcpCatalogPanel.clientSecret')}
                  </FieldLabel>
                  <Input
                    id={`${fieldId}-client-secret`}
                    type="password"
                    {...form.register('oauthClientSecret')}
                    disabled={isSubmitting}
                  />
                  {entry?.hasOauthClientSecret && (
                    <FieldDescription>
                      {t('admin.mcpCatalogPanel.clientSecretStored')}
                    </FieldDescription>
                  )}
                </Field>
                {credentialsWillChange && (
                  <p className="text-body-sm text-warning-foreground">
                    {t('admin.mcpCatalogPanel.changingCredentialsSignsOut')}
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </form>
    </FormSidePanel>
  );
}

McpCatalogFormSheet.displayName = 'McpCatalogFormSheet';
